import React, { useEffect, useRef, useState } from "react";
import { isoToLangDisplay, langDisplayToIso } from "../utils/tigerTime";
import { getTigerResource } from "../utils/tigerResource";

interface LangDateInputProps {
  /** ISO `YYYY-MM-DD` (or `""`). Same value contract as `<input type="date">`. */
  value: string;
  /** Called with the new ISO `YYYY-MM-DD` (or `""` when cleared). */
  onChange: (iso: string) => void;
  disabled?: boolean;
  /** Applied to the visible text field so call sites keep their existing sizing. */
  style?: React.CSSProperties;
  title?: string;
  id?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  /** ISO `YYYY-MM-DD` bounds forwarded to the native picker (as `<input type=date>` min/max). */
  min?: string;
  max?: string;
}

/** Style properties that size/place the field in its parent's layout. They have
 *  to land on the wrapper span, not the inner `<input>`: callers style this
 *  component as if it were a plain `<input>`, so `flex: 1` on the inner element
 *  would be ignored (the wrapper is not a flex container) and `width: 86` would
 *  size the input inside a wrapper that still shrink-wraps its own content. The
 *  paint properties (font, padding, border, background, colour, opacity …) stay
 *  on the input, which then fills the wrapper. */
const WRAPPER_STYLE_PROPS = [
  "display",
  "flex",
  "flexGrow",
  "flexShrink",
  "flexBasis",
  "width",
  "minWidth",
  "maxWidth",
  "alignSelf",
  "justifySelf",
  "order",
  "gridColumn",
  "gridRow",
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
] as const satisfies readonly (keyof React.CSSProperties)[];

function splitStyle(style?: React.CSSProperties) {
  const wrapper: React.CSSProperties = {};
  const field: React.CSSProperties = { ...style };
  if (style) {
    for (const key of WRAPPER_STYLE_PROPS) {
      if (style[key] === undefined) continue;
      wrapper[key] = style[key] as never;
      delete field[key];
    }
  }
  return { wrapper, field };
}

/**
 * Date field that honours `TIGER_LANG`: the visible text shows/accepts the date
 * in the locale order (US → MM/DD/YYYY, otherwise DD/MM/YYYY), which a native
 * `<input type="date">` cannot do (its display is fixed to the browser locale).
 *
 * A calendar button opens the OS date picker via an internal (visually hidden)
 * `<input type="date">`; that same element keeps the value contract as ISO
 * `YYYY-MM-DD`, so call sites and their tests can keep driving it by type=date.
 * Typed text is committed on blur / Enter and reverted if it isn't a real date.
 */
export default function LangDateInput({
  value,
  onChange,
  disabled,
  style,
  title,
  id,
  "aria-label": ariaLabel,
  "data-testid": dataTestid,
  min,
  max,
}: LangDateInputProps) {
  const [text, setText] = useState(() => isoToLangDisplay(value));
  const pickerRef = useRef<HTMLInputElement>(null);

  // Keep the visible text in sync when the value or locale changes elsewhere
  // (external Set/Apply-All updates, prepopulation, a US/UK flip after bootstrap).
  useEffect(() => {
    setText(isoToLangDisplay(value));
  }, [value]);

  const commit = () => {
    const iso = langDisplayToIso(text);
    if (iso === null) {
      // Not a real date — revert to the last good value.
      setText(isoToLangDisplay(value));
      return;
    }
    if (iso !== value) onChange(iso);
    setText(isoToLangDisplay(iso));
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    // showPicker() must run from a user gesture (this click). Fall back to a
    // synthetic click for older engines that lack it.
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else el.click();
    } catch {
      el.click();
    }
  };

  // Layout: a wrapper that carries the caller's sizing (see splitStyle) with the
  // input filling it, room reserved on the input's right (paddingRight), and the
  // calendar icon absolutely positioned and vertically centered against that
  // right edge (top:0/bottom:0 + flex centering). Verified in a headless browser
  // to sit inline at the right edge, matching the native time field's icon.
  const { wrapper: wrapperStyle, field: fieldStyle } = splitStyle(style);

  return (
    <span style={{ position: "relative", display: "inline-block", verticalAlign: "middle", ...wrapperStyle }}>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        disabled={disabled}
        title={title}
        id={id}
        aria-label={ariaLabel}
        data-testid={dataTestid}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Escape") {
            setText(isoToLangDisplay(value));
            (e.target as HTMLInputElement).blur();
          }
        }}
        style={{ ...fieldStyle, width: "100%", boxSizing: "border-box", paddingRight: 18 }}
      />
      <button
        type="button"
        disabled={disabled}
        title={getTigerResource("TIGER_UI_PICK_A_DATE")}
        aria-label={getTigerResource("TIGER_UI_PICK_A_DATE")}
        onClick={openPicker}
        onMouseDown={(e) => e.preventDefault()} // don't steal focus / fire the text blur
        style={{
          position: "absolute",
          right: 2,
          top: 0,
          bottom: 0,
          // Override the global `button { min-width: 70px; padding: 5px 16px }`
          // rule, which would otherwise blow the icon button up to 70px wide and
          // push its centred glyph over the date text.
          width: 16,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          background: "transparent",
          outline: "none",
          padding: 0,
          margin: 0,
          borderRadius: 0,
          color: "#555",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.4 : 1,
        }}
      >
        {/* Neutral monochrome calendar glyph (the 📅 emoji renders as a blue box). */}
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" style={{ display: "block" }}>
          <rect x="2" y="3" width="12" height="11" rx="1.5" />
          <line x1="2" y1="6.5" x2="14" y2="6.5" />
          <line x1="5" y1="1.5" x2="5" y2="4" strokeLinecap="round" />
          <line x1="11" y1="1.5" x2="11" y2="4" strokeLinecap="round" />
        </svg>
      </button>
      {/* Visually hidden native picker: source of the OS calendar popup and the
          ISO value contract. Kept in the DOM (not display:none) so showPicker()
          works. */}
      <input
        ref={pickerRef}
        type="date"
        value={value || ""}
        min={min}
        max={max}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 1,
          height: 1,
          opacity: 0,
          border: 0,
          padding: 0,
          pointerEvents: "none",
        }}
      />
    </span>
  );
}
